<template>
  <div>
    <GuideVideoBanner 
      v-if="props.guideData"
      :text="props.guideData.content.text"
      :subtext="props.guideData.content.subText"
      :video-url="props.guideData.content.videoUrl"
      :video-thumbnail="props.guideData.content.videoThumbnail"
      @close="handleGuideDismiss"
    />
    <div class="org-role-tab">
      <OrganizationRolesTable 
        :dataStream="rolesData" 
        :pagination="paginationModel"
        :chatterData="chatterData"
        :totalChatterData="totalChatterData"
        :useNewBadge="props.useNewBadge"
        :initialFilters="currentFilters"
        @onPageChange="handlePageChange"
        @onFilterChange="handleFilterChange"
        @onSearchChange="handleSearchChange"
        @detach="handleDetach"
        @openChatterDrawer="handleOpenChatterDrawer"
        @requestMoreCheckpointsChatter="handleRequestMoreChatter"
        @onRowOperation="handleRowOperation"
        @onRoleDrillDown="handleRoleDrillDown"
      />
    </div>
    
    <!-- Dialogs -->
    <ActivateRole
      v-model="showActivateRoleDialog"
      @onSave="activateRole"
      :role="currentRole"
    />
    <RetiredRole
      v-model="showRetireRoleDialog"
      @onSave="retireRole"
      :role="currentRole"
    />
    <ReActivateRole
      v-model="showReActivateRoleDialog"
      @onSave="reActivateRole"
      :role="currentRole"
    />
    <RenameRole
      v-model="showRenameRoleDialog"
      @onSave="renameRole"
      :role="currentRole?.data?.name || ''"
    />
    <DeleteRole
      v-model="showDeleteRoleDialog"
      @onDelete="deleteRole"
      :role="currentRole"
    />
    <ArchivePopUp
      :action="archiveAction"
      type="role"
      :show="showArchiveDialog"
      :curretObj="currentRole"
      @onClose="showArchiveDialog = false"
      @onArchive="onArchiveRole"
    />
    <AttachedResponsibility
      v-model="showAttachResponsibilityDialog"
      @onSave="handleAttachResponsibilityToRole"
      :items="attachableResponsibilities"
      @item-selected="handleResponsibilitySelected"
      @add-item-selected="handleAddNewResponsibility"
      :existingNamesList="existingResponsibilitiesNames"
      :parentName="currentRole?.role_name || currentRole?.data?.name || ''"
      :openDefault="openDefaultAttachDialog"
      @input="closeAttachResponsibilityDialog"
      :type="
        showAttachResponsibilitiesPicker
          ? 'attach_responsibility_role_new_picker'
          : 'attach_responsibility_role'
      "
      @load-more="loadMoreResponsibilities"
      @search="searchResponsibilities"
      :hasMoreData="hasMoreResponsibilitiesData"
      :isLoadingMore="isLoadingMoreResponsibilities"
      :isAssigning="isAssigningResponsibility"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, getCurrentInstance, computed } from "vue";
import { 
  getListOfRolesV2, 
  detachOrgObject, 
  getRoleChatter,
  updateActiveRole,
  updateRetiredRole,
  updateReActiveRole,
  updateNameRole,
  deleteReadinessRole,
  deleteReadinessRoleWithResponsibilities,
  updateArchiveRole,
  updateUnArchiveRole,
  fetchAttachableResponsibilities,
  attachResponsibilityToRole,
  getChatterList,
} from "@/api";
import OrganizationRolesTable from "@components/OrganizationRolesTable/OrganizationRolesTable.vue";
import ActivateRole from "@components/RolesCatalog/ActivateRole.vue";
import RetiredRole from "@components/RolesCatalog/RetiredRole.vue";
import ReActivateRole from "@components/RolesCatalog/ReActivateRole.vue";
import RenameRole from "@components/RolesCatalog/RenameRole.vue";
import DeleteRole from "@components/RolesCatalog/DeleteRole.vue";
import ArchivePopUp from "@components/common/ArchivePopUp.vue";
import GuideVideoBanner from "@components/GuideVideoBanner/GuideVideoBanner.vue";
import AttachedResponsibility from "@components/RolesCatalog/AttachedRole.vue";

const { proxy } = getCurrentInstance();

const emit = defineEmits(['drilldown', 'guide-close']);

// Props
const props = defineProps({
  guideData: {
    type: Object,
    default: null,
  },
  useNewBadge: {
    type: Boolean,
    default: false,
  },
});

const rolesData = ref([]);
const paginationModel = ref({
  page: 1,
  total: 0,
  perPage: 10,
});
const isLoading = ref(false);
const accountId = ref(null);
const currentFilters = ref(["draft", "active"]); // Default filters
const currentSearch = ref('');
const chatterData = ref([]);
const totalChatterData = ref(0);
const isInitialLoad = ref(true); // Track if initial load has happened

// Dialog states
const showActivateRoleDialog = ref(false);
const showRetireRoleDialog = ref(false);
const showReActivateRoleDialog = ref(false);
const showRenameRoleDialog = ref(false);
const showDeleteRoleDialog = ref(false);
const showArchiveDialog = ref(false);
const archiveAction = ref(null);
const currentRole = ref(null);

// Attach responsibility to role state
const showAttachResponsibilityDialog = ref(false);
const attachableResponsibilities = ref([]);
const hasMoreResponsibilitiesData = ref(false);
const isLoadingMoreResponsibilities = ref(false);
const isAssigningResponsibility = ref(false);
const selectedResponsibilityId = ref(null);
const currentRoleId = ref(null);
const responsibilitiesPage = ref(1);
const responsibilitiesSearchTerm = ref('');
const existingResponsibilitiesNames = ref([]);
const openDefaultAttachDialog = ref(false);

// Convert frontend filters to API format
const convertFiltersToAPIFormat = (filters) => {
  const apiFilters = {};
  const statusOptions = ["draft", "active", "retired", "inactive"];
  
  // Extract status filters
  const statusFilters = filters.filter(f => statusOptions.includes(f));
  if (statusFilters.length > 0) {
    apiFilters.status = statusFilters;
  }
  
  // Archived filter - checking for labels (what RadioInput emits)
  if (filters.includes("Show")) {
    apiFilters.archived = true;
  } else if (filters.includes("Do not Show")) {
    apiFilters.archived = false;
  }
  
  // Attachments filters - checking for labels (what RadioInput emits)
  if (filters.includes("No Positions")) {
    apiFilters.positions = "no";
  }
  if (filters.includes("No Responsibilities")) {
    apiFilters.responsibilities = "no";
  }
  
  // Timeline filters - checking for labels (what RadioInput emits)
  if (filters.includes("Recently Activated")) {
    apiFilters.recently_activated = true;
  }
  if (filters.includes("Recently Retired")) {
    apiFilters.recently_retired = true;
  }
  if (filters.includes("Recently Created")) {
    apiFilters.recently_created = true;
  }
  
  return apiFilters;
};

// Fetch roles from API
const loadRoles = async ({ limit = 10, offset = 0, search = '', filters = {}, ordering = 'name', skipLoader = false } = {}) => {
  try {
    if (!skipLoader) {
      proxy.$loader.show();
    }
    isLoading.value = true;
    
    // Get account ID from localStorage
    if (!accountId.value) {
      accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    }
    
    // Call the API directly
    const response = await getListOfRolesV2(
      accountId.value,
      limit,
      offset,
      search,
      filters,
      ordering === "data" ? "name" : ordering
    );
    
    const roleList = response?.data?.results || [];
    const totalCount = response?.data?.count || 0;
    
    // Transform the data to match the expected format for OrganizationRolesTable
    rolesData.value = roleList.map((el) => {
      return {
        id: el.id || null,
        role_code: el.code || "",
        role_name: el.name || "",
        status: el.status || "inactive",
        attached_position_count: el.position_attachment_count || 0,
        attached_res_count: el.responsibility_attachment_count || 0,
        // New badge component fields
        active_attached_position_count: el.active_attached_position_count || 0,
        inactive_attached_position_count: el.inactive_attached_position_count || 0,
        active_attached_responsibilities_count: el.active_attached_responsibilities_count || 0,
        inactive_attached_responsibilities_count: el.inactive_attached_responsibilities_count || 0,
        archived: el.archived || false,
        draft_responsibilities_count: el.draft_responsibilities_count || 0,
      };
    });
    
    // Update pagination
    paginationModel.value = {
      page: Math.floor(offset / limit) + 1,
      total: totalCount,  // Total number of roles, not pages
      perPage: limit,
    };
  } catch (error) {
    console.error("Error loading roles:", error);
    rolesData.value = [];
  } finally {
    isLoading.value = false;
    if (!skipLoader) {
      proxy.$loader.hide();
    }
  }
};

// Handle page change from table
const handlePageChange = async (pageData) => {
  const offset = (pageData.page - 1) * paginationModel.value.perPage;
  const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
  await loadRoles({
    limit: paginationModel.value.perPage,
    offset: offset,
    search: currentSearch.value,
    filters: apiFilters,
    ordering: 'name',
  });
};

// Handle filter change from table
const handleFilterChange = async (filters) => {
  currentFilters.value = filters;
  const apiFilters = convertFiltersToAPIFormat(filters);
  // Reset to page 1 when filters change
  await loadRoles({
    limit: paginationModel.value.perPage,
    offset: 0,
    search: currentSearch.value,
    filters: apiFilters,
    ordering: 'name',
  });
  // Mark that initial load is complete
  isInitialLoad.value = false;
};

// Handle search change from table
const handleSearchChange = async (searchTerm) => {
  currentSearch.value = searchTerm;
  const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
  // Reset to page 1 when search changes
  await loadRoles({
    limit: paginationModel.value.perPage,
    offset: 0,
    search: searchTerm,
    filters: apiFilters,
    ordering: 'name',
  });
};

// Handle detach action from table
const handleDetach = async (detachData) => {
  try {
    // Note: The modal already sets $loader.setAppLoading(true) when detach is clicked
    
    const targetObj = detachData.target;
    const detachObj = detachData.detachObj;
    
    if (!accountId.value) {
      accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    }
    
    if (targetObj.objectType.toLowerCase() === "role") {
      if (detachObj.type.toLowerCase() === "position") {
        // Detach role from position
        const payload = {
          childType: targetObj.objectType.toLowerCase(),
          childId: targetObj.objectId,
          parentData: {
            parent_id: detachObj.id,
          },
        };
        await detachOrgObject(accountId.value, payload);
      } else if (detachObj.type.toLowerCase() === "responsibility") {
        // Detach responsibility from role
        const payload = {
          childType: "responsibility",
          childId: detachObj.id,
          parentData: {
            parent_id: targetObj.objectId,
          },
        };
        await detachOrgObject(accountId.value, payload);
      }
    }
    
    // Reload the roles data to reflect the changes
    const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
    await loadRoles({
      limit: paginationModel.value.perPage,
      offset: (paginationModel.value.page - 1) * paginationModel.value.perPage,
      search: currentSearch.value,
      filters: apiFilters,
      ordering: 'name',
    });
    
    // Call the callback function to close the modal AFTER reloading data
    if (detachData.callBackFunc) {
      detachData.callBackFunc();
    }
  } catch (error) {
    console.error("Error detaching:", error);
  } finally {
    // Turn off the loader to re-enable buttons
    proxy.$loader.setAppLoading(false);
  }
};

// Handle opening chatter drawer
const handleOpenChatterDrawer = async (roleData) => {
  try {
    // Reset chatter data
    chatterData.value = [];
    totalChatterData.value = 0;
    
    if (!accountId.value) {
      accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    }
    
    const roleId = roleData.id;
    const page = 1;
    
    const { data } = await getChatterList(accountId.value, roleId, "role", page);
    
    if (data && data.count) {
      totalChatterData.value = data.count;
    }
    
    if (data && data.results) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const nameParts = el.recorded_by_name.split(" ");
        
        const newEl = {
          id: el.id,
          avatar: nameParts.length === 2 
            ? {
                picture: "",
                firstName: nameParts[0],
                lastName: nameParts[1],
              }
            : {
                picture: "",
                firstName: el.recorded_by_name,
                lastName: "",
              },
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };
        
        return newEl;
      });
      
      chatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error loading chatter data:", error);
  }
};

// Handle loading more chatter messages
const handleRequestMoreChatter = async (roleData) => {
  try {
    if (!accountId.value) {
      accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    }
    
    const roleId = roleData.id;
    const currentLength = chatterData.value.length;
    const page = Math.ceil(currentLength / 10) + 1;
    
    const { data } = await getChatterList(accountId.value, roleId, "role", page);
    
    if (data && data.results) {
      const chatterItems = data.results.map((el) => {
        const date = new Date(el.recorded_on);
        const milliseconds = date.getTime();
        const millisecondsString = milliseconds.toString();
        const nameParts = el.recorded_by_name.split(" ");
        
        const newEl = {
          id: el.id,
          avatar: nameParts.length === 2 
            ? {
                picture: "",
                firstName: nameParts[0],
                lastName: nameParts[1],
              }
            : {
                picture: "",
                firstName: el.recorded_by_name,
                lastName: "",
              },
          IsExIQtiveBot: false,
          message: el.note,
          created_on: millisecondsString,
          IsHistory: el.is_history,
        };
        
        return newEl;
      });
      
      chatterData.value.push(...chatterItems);
    }
  } catch (error) {
    console.error("Error loading more chatter data:", error);
  }
};

// Handle dropdown menu operations
const handleRowOperation = async (operation) => {
  const action = operation.item?.value;
  currentRole.value = operation.data;
  
  switch (action) {
    case 'activate_role':
      showActivateRoleDialog.value = true;
      break;
    case 'retired_role':
      showRetireRoleDialog.value = true;
      break;
    case 're_activate_role':
      showReActivateRoleDialog.value = true;
      break;
    case 'attach_responsibility_role':
      currentRoleId.value = currentRole.value?.id || currentRole.value?.data?.id;
      openDefaultAttachDialog.value = true;
      showAttachResponsibilityDialog.value = true;
      responsibilitiesPage.value = 1;
      responsibilitiesSearchTerm.value = '';
      await fetchAttachableResponsibilitiesList();
      break;
    case 'rename_role':
      showRenameRoleDialog.value = true;
      break;
    case 'delete_role':
      showDeleteRoleDialog.value = true;
      break;
    case 'archive_action':
      archiveAction.value = 'archive';
      showArchiveDialog.value = true;
      break;
    case 'unarchive_action':
      archiveAction.value = 'unarchive';
      showArchiveDialog.value = true;
      break;
    default:
      console.log('Unknown action:', action);
  }
};

const handleRoleDrillDown = (roleData) => {
  // Navigate to role detail route using router
  if (proxy?.$router && roleData.id) {
    proxy.$router.push({
      name: 'RoleDetailChild',
      params: { roleId: roleData.id },
      query: { roleTab: 'purpose' }
    }).catch(() => {});
  }
};

// Dialog action handlers
const activateRole = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    if (!accountId.value) {
      accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
    }
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    const payload = {
      account_id: accountId.value,
      role_id: idRole,
    };
    await updateActiveRole(accountId.value, idRole, payload);
    showActivateRoleDialog.value = false;
    currentRole.value = null;
    // Reload data
    await reloadCurrentPage();
  } catch (error) {
    console.error('Error activating role:', error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const retireRole = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    const nameRole = currentRole.value?.data?.name;
    const payload = {
      name: nameRole,
      status: "retired",
      account: accountId.value,
    };
    await updateRetiredRole(accountId.value, idRole, payload);
    showRetireRoleDialog.value = false;
    currentRole.value = null;
    // Reload data
    await reloadCurrentPage();
  } catch (error) {
    console.error('Error retiring role:', error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const reActivateRole = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    const nameRole = currentRole.value?.data?.name;
    const payload = {
      name: nameRole,
      status: "inactive",
      account: accountId.value,
    };
    await updateReActiveRole(accountId.value, idRole, payload);
    showReActivateRoleDialog.value = false;
    currentRole.value = null;
    // Reload data
    await reloadCurrentPage();
  } catch (error) {
    console.error('Error re-activating role:', error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const renameRole = async (newName) => {
  try {
    proxy.$loader.setAppLoading(true);
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    const payload = {
      name: newName,
    };
    await updateNameRole(accountId.value, idRole, payload);
    showRenameRoleDialog.value = false;
    currentRole.value = null;
    // Reload data
    await reloadCurrentPage();
  } catch (error) {
    console.error('Error renaming role:', error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const deleteRole = async (deleteDraftResponsibilities) => {
  try {
    proxy.$loader.setAppLoading(true);
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    const payload = {
      delete_draft_responsibilities: deleteDraftResponsibilities,
    };
    
    if (deleteDraftResponsibilities) {
      await deleteReadinessRoleWithResponsibilities(accountId.value, idRole, payload);
    } else {
      await deleteReadinessRole(accountId.value, idRole);
    }
    
    showDeleteRoleDialog.value = false;
    currentRole.value = null;
    // Reload data
    await reloadCurrentPage();
  } catch (error) {
    console.error('Error deleting role:', error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

const onArchiveRole = async () => {
  try {
    proxy.$loader.setAppLoading(true);
    const idRole = currentRole.value?.id || currentRole.value?.data?.id;
    const objType = 'role';
    
    if (archiveAction.value === 'archive') {
      await updateArchiveRole(accountId.value, objType, idRole);
    } else {
      await updateUnArchiveRole(accountId.value, objType, idRole);
    }
    
    showArchiveDialog.value = false;
    currentRole.value = null;
    // Reload data
    await reloadCurrentPage();
  } catch (error) {
    console.error('Error archiving/unarchiving role:', error);
  } finally {
    proxy.$loader.setAppLoading(false);
  }
};

// Helper to reload current page
const reloadCurrentPage = async () => {
  const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
  await loadRoles({
    limit: paginationModel.value.perPage,
    offset: (paginationModel.value.page - 1) * paginationModel.value.perPage,
    search: currentSearch.value,
    filters: apiFilters,
    ordering: 'name',
  });
};

// Attach responsibility to role methods
const fetchAttachableResponsibilitiesList = async (isLoadMore = false) => {
  if (!currentRoleId.value || !accountId.value) return;
  
  const limit = 10;
  const offset = (responsibilitiesPage.value - 1) * limit;
  const name = responsibilitiesSearchTerm.value || '';
  
  try {
    if (!isLoadMore) {
      attachableResponsibilities.value = [];
    }
    isLoadingMoreResponsibilities.value = true;
    
    const response = await fetchAttachableResponsibilities(
      accountId.value,
      currentRoleId.value,
      limit,
      offset,
      name
    );
    
    const newItems = response.data.results.map((item) => ({
      id: item.id,
      code: item.code_str?.match(/\d+/)?.[0] || '',
      name: item.name,
      status: item.status,
      attached: item?.attached_to_role
    }));
    
    if (isLoadMore && attachableResponsibilities.value) {
      attachableResponsibilities.value = [...attachableResponsibilities.value, ...newItems];
    } else {
      attachableResponsibilities.value = newItems;
    }
    
    const totalCount = response.data.count || 0;
    hasMoreResponsibilitiesData.value = (responsibilitiesPage.value * limit) < totalCount;
    
    // Extract existing names for validation
    existingResponsibilitiesNames.value = attachableResponsibilities.value.map(item => item.name);
  } catch (error) {
    console.error("Error fetching attachable responsibilities:", error);
  } finally {
    isLoadingMoreResponsibilities.value = false;
  }
};

const handleResponsibilitySelected = (responsibility) => {
  if (responsibility && Object.prototype.hasOwnProperty.call(responsibility, "id")) {
    selectedResponsibilityId.value = responsibility.id;
  }
};

const handleAddNewResponsibility = async (item) => {
  try {
    proxy.$loader.show();
    const payload = {
      name: item.name,
      accountId: accountId.value,
    };
    const newResponsibility = await proxy.$store.dispatch(
      "readinessCatalogsStore/createResponsibility",
      payload
    );
    selectedResponsibilityId.value = newResponsibility.data.id;
    proxy.$loader.hide();
    // handleAttachResponsibilityToRole will manage its own loader and close the dialog
    await handleAttachResponsibilityToRole();
  } catch (error) {
    console.error("Error creating responsibility:", error);
    proxy.$loader.hide();
  }
};

const handleAttachResponsibilityToRole = async () => {
  if (!selectedResponsibilityId.value || !currentRoleId.value) return;
  
  try {
    isAssigningResponsibility.value = true;
    proxy.$loader.setAppLoading(true);
    
    const payload = {
      attachment_id: selectedResponsibilityId.value,
    };
    
    await attachResponsibilityToRole(accountId.value, currentRoleId.value, payload);
    
    showAttachResponsibilityDialog.value = false;
    selectedResponsibilityId.value = null;
    responsibilitiesPage.value = 1;
    responsibilitiesSearchTerm.value = '';
    
    // Reload roles data - skip loader since we're already managing it
    const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
    await loadRoles({
      limit: paginationModel.value.perPage,
      offset: (paginationModel.value.page - 1) * paginationModel.value.perPage,
      search: currentSearch.value,
      filters: apiFilters,
      ordering: 'name',
      skipLoader: true,
    });
  } catch (error) {
    console.error("Error attaching responsibility:", error);
  } finally {
    isAssigningResponsibility.value = false;
    proxy.$loader.setAppLoading(false);
  }
};


const loadMoreResponsibilities = async () => {
  if (isLoadingMoreResponsibilities.value || !hasMoreResponsibilitiesData.value) {
    return;
  }
  responsibilitiesPage.value += 1;
  await fetchAttachableResponsibilitiesList(true);
};

const searchResponsibilities = async (searchTerm) => {
  responsibilitiesSearchTerm.value = searchTerm;
  responsibilitiesPage.value = 1;
  await fetchAttachableResponsibilitiesList(false);
};

const closeAttachResponsibilityDialog = () => {
  showAttachResponsibilityDialog.value = false;
  openDefaultAttachDialog.value = false;
  selectedResponsibilityId.value = null;
  responsibilitiesPage.value = 1;
  responsibilitiesSearchTerm.value = '';
};

// Computed property for feature flag
const showAttachResponsibilitiesPicker = computed(() => {
  return proxy.$store.getters["featureFlags/ideaMDP389"];
});

// Initialize data on component mount
onMounted(async () => {
  // Initialize accountId
  if (!accountId.value) {
    accountId.value = JSON.parse(localStorage.getItem("currentAccountID"));
  }
  
  // Wait for nextTick to ensure child component has been created and emitted initial filters
  // The child component emits filters in its created() hook via applySavedFilters()
  await proxy.$nextTick();
  
  // Use a small delay to ensure child component's applySavedFilters() has completed
  // and emitted the filters via onFilterChange event before we check if we need to load
  await new Promise(resolve => setTimeout(resolve, 50));
  
  // Only load if initial load hasn't happened yet (child hasn't emitted filters)
  // This ensures we don't double-load: either child emits filters and loads, or we load with defaults
  if (isInitialLoad.value) {
    // If child didn't emit filters (shouldn't happen, but fallback), use default filters
    const apiFilters = convertFiltersToAPIFormat(currentFilters.value);
    await loadRoles({
      limit: 10,
      offset: 0,
      search: '',
      filters: apiFilters,
      ordering: 'name',
    });
    isInitialLoad.value = false;
  }
});

// Expose methods for parent components to use
defineExpose({
  loadRoles,
  rolesData,
  paginationModel,
  isLoading,
});
</script>

<style>
.org-role-tab {
  margin-top: 15px;
}
</style>

<style scoped>
.guide-video-banner {
  margin-top: 15px;
  margin-bottom: 15px;
}
</style>